/* eslint-disable react/jsx-no-bind */
import classNames from "classnames";
import Badge from "~/components/shared/Badge";
import { NavLink, useMatches, useSearchParams } from "react-router-dom";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { Build } from "~/components/build/Show/lib/types/Build";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import { useWaterfallAvailableStore } from "~/components/build/Show/lib/useWaterfallAvailableStore";
import {
  ANNOTATIONS_VIEWS,
  BuildView,
  STEPS_DISPLAY_MODES,
  SUMMARY_DISPLAY_MODES,
  TESTS_VIEWS,
  resolveStepsDisplayMode,
} from "~/components/build/Show/lib/types/BuildView";
import { urlForView, withoutDrawerParams } from "~/components/build/Show/lib/urlForView";
import { parseBuildSearch } from "~/components/build/Show/lib/searchParams";
import { useMemo } from "react";
import FailedTestExecutionsIndicator from "~/components/job/Job/FailedTestExecutionsIndicator";
import { motion } from "framer-motion";
import Count from "~/components/build/Show/components/Count";
import AnnotationsPanelToggle from "~/components/build/Show/components/AnnotationsPanelToggle";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { useMobileBreakpoint } from "~/components/build/Show/lib/useBreakpoints";
import {
  DockPosition,
  useAdjustedDockPosition,
} from "~/components/build/Show/lib/useAdjustedDockPosition";

const BuildNavLink = ({
  children,
  activeViews,
  url,
}: {
  children: React.ReactNode;
  activeViews: ReadonlySet<BuildView>;
  url: string;
}) => {
  const matches = useMatches();
  const isActive = matches.some((match) => activeViews.has(match.id as BuildView));

  if (Features.BuildPageListView) {
    return (
      <NavLink
        to={url}
        className={classNames(
          "focus:outline-hidden relative inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-2 py-1 text-sm font-normal text-[#334155] !no-underline transition-[background-color,box-shadow,color] duration-100 hover:!text-[#0F172A] hover:no-underline focus:text-[#0F172A] focus:no-underline focus-visible:outline-offset-0",
          isActive
            ? "bg-white shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.05),0_0_0_1px_rgba(0,0,0,0.08),0_1px_0_0_rgba(0,0,0,0.1)]"
            : "bg-transparent hover:bg-white/50",
        )}
      >
        {children}
      </NavLink>
    );
  }

  return (
    <NavLink
      to={url}
      className="btn btn-tab relative px-2 text-sm text-current transition-colors focus:text-current"
    >
      {children}
      {isActive && (
        <motion.div
          layoutId="build-nav-link-active-indicator"
          className="absolute left-0 right-0 top-full h-[3px] bg-purple-600"
          transition={{ type: "spring", duration: 0.2, bounce: 0 }}
        />
      )}
    </NavLink>
  );
};

export function showTestsTabPill(build: Build) {
  // The "new" pill is cleared when the tests tab turbo frame is loaded in
  // the TestEngine component.
  const isMarketingUrl = build.testEngine.testTabUrls?.tabUrl?.includes("marketing");
  const isPillDismissed = localStorage.getItem("hide-test-tab-pill") === "true";
  return isMarketingUrl && !isPillDismissed;
}

function useStepsView() {
  const currentModeId = useBuildStore((state) => state.stepsDisplayMode);
  return resolveStepsDisplayMode(currentModeId);
}

function BuildContentNavigation() {
  const { build } = useBuild();
  const [searchParams] = useSearchParams();
  const stepsView = useStepsView();
  const isMobile = useMobileBreakpoint();
  const dockPosition = useAdjustedDockPosition();

  // When the drawer is centre-docked it covers the page content, so navigating
  // between top-level tabs should close it. Strip drawer params (jid, sid,
  // open, tab) from nav links so a click navigates without re-opening the
  // drawer on the new page. Side-docked (Right) and bottom-docked drawers
  // remain visible alongside the content, so we preserve drawer params there.
  const linkParams = useMemo(() => {
    const filtered =
      dockPosition === DockPosition.Center ? withoutDrawerParams(searchParams) : searchParams;
    return parseBuildSearch(Object.fromEntries(filtered.entries()));
  }, [dockPosition, searchParams]);

  const navItems = useMemo(() => {
    const items: {
      label: string;
      url: string;
      activeViews: ReadonlySet<BuildView>;
      indicator?: React.ReactNode;
      pill?: React.ReactNode;
      count?: React.ReactNode;
    }[] = [
      {
        label: "Summary",
        url: urlForView(build.path, BuildView.Summary, linkParams),
        activeViews: SUMMARY_DISPLAY_MODES,
      },
    ];

    if (Features.BuildPageListView && !isMobile) {
      // Individual top-level tabs for each step view
      items.push({
        label: "List",
        url: urlForView(build.path, BuildView.List, linkParams),
        activeViews: new Set([BuildView.List]),
      });

      items.push({
        label: "Table",
        url: urlForView(build.path, BuildView.Table, linkParams),
        activeViews: new Set([BuildView.Table]),
      });

      if (!Features.DisableStepsCanvasView) {
        items.push({
          label: "Canvas",
          url: urlForView(build.path, BuildView.Canvas, linkParams),
          activeViews: new Set([BuildView.Canvas]),
        });
      }

      if (!Features.DisableStepsWaterfallView) {
        items.push({
          label: "Waterfall",
          url: urlForView(build.path, BuildView.Waterfall, linkParams),
          activeViews: new Set([BuildView.Waterfall]),
        });
      }
    } else {
      // Single "Steps" tab that expands into the SubNavBar in StepsLayout
      items.push({
        label: "Steps",
        url: urlForView(build.path, stepsView, linkParams),
        activeViews: STEPS_DISPLAY_MODES,
      });
    }

    if (!Features.BuildPageDockedAnnotations) {
      const counts = getAnnotationCounts(build);
      const annotationCount = counts.totalCount;

      items.push({
        url: urlForView(build.path, BuildView.Annotations, linkParams),
        activeViews: ANNOTATIONS_VIEWS,
        label: "Annotations",
        count: annotationCount > 0 && (
          <span className={Features.BuildPageListView ? "" : "ml-1"}>
            <Count data-testid="annotations-count">{annotationCount}</Count>
          </span>
        ),
      });
    }

    if (build.testEngine.testTabUrls?.tabUrl && !Features.TestEngineDisableBuildPageTestTab) {
      items.push({
        label: "Tests",
        url: urlForView(build.path, BuildView.Tests, linkParams),
        activeViews: TESTS_VIEWS,
        indicator: Features.BuildPageListView ? (
          <FailedTestExecutionsIndicator build={build} translateX="4px" translateY="-4px" />
        ) : (
          <FailedTestExecutionsIndicator build={build} translateX="1px" translateY="8px" />
        ),
        pill: showTestsTabPill(build) && (
          <span className="badge badge--outline tabular-numerals ml-1">New</span>
        ),
      });
    }

    return items;
  }, [build, isMobile, linkParams, stepsView]);

  if (!Features.BuildSidebar) {
    return <ClassicNavigation />;
  }

  const renderedNavItems = navItems.map(({ label, url, activeViews, indicator, pill, count }) => (
    <BuildNavLink key={label} url={url} activeViews={activeViews}>
      {indicator}
      {label}
      {pill}
      {count}
    </BuildNavLink>
  ));

  return (
    <nav data-testid="BuildContentNavigation" className="flex min-w-0 flex-1 items-center">
      {Features.BuildPageListView ? (
        <div className="my-1 inline-flex items-center gap-0.5 rounded-md bg-[#e5e7eb80] p-0.5">
          {renderedNavItems}
        </div>
      ) : (
        renderedNavItems
      )}
      {Features.BuildPageDockedAnnotations && (
        <div className="ml-auto flex items-center">
          <AnnotationsPanelToggle />
        </div>
      )}
    </nav>
  );
}

/**
 * Navigation for users without BuildSidebar feature
 */
function ClassicNavigation() {
  const { build } = useBuild();
  const waterfallAvailable = useWaterfallAvailableStore((state) => state.waterfallAvailable);

  const listHref = build.path;
  const testsHref = "./tests";
  const canvasHref = "./canvas";
  const waterfallHref = "./waterfall";
  const showBuildTests =
    build.testEngine.testTabUrls?.tabUrl && !Features.TestEngineDisableBuildPageTestTab;

  return (
    <nav className="Tablist border-gray mb-[-1px] flex border-b" data-testid="buildViewTabsNav">
      <NavLink
        to={listHref}
        end={true}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Jobs</span>
      </NavLink>

      <NavLink
        to={canvasHref}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Canvas</span>
      </NavLink>

      <NavLink
        to={waterfallHref}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Waterfall</span>
        {!waterfallAvailable && (
          <Badge outline={false} className="white bg-purple">
            Upgrade
          </Badge>
        )}
      </NavLink>

      {showBuildTests && (
        <NavLink
          to={testsHref}
          className={({ isActive }) =>
            classNames("btn btn-tab relative text-current focus:text-current", {
              "btn-tab--active": isActive,
            })
          }
        >
          <FailedTestExecutionsIndicator build={build} />
          <span>Tests</span>
          {showTestsTabPill(build) && (
            <span className="badge badge--outline tabular-numerals ml-1">New</span>
          )}
        </NavLink>
      )}
    </nav>
  );
}

export default BuildContentNavigation;
